/**
 * Notification dropdown component for the header.
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Check, CheckCheck, X, AlertCircle, Info, CheckCircle, XCircle, Clock, Settings } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import { Notification } from '../../services/notificationAPI';
import { NotificationSettingsModal } from './NotificationSettingsModal';
import { logError } from '../../utils/errorLogger';

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  position: { top: number; right: number };
  theme: 'dark' | 'light';
  triggerRef?: React.RefObject<HTMLElement | null>;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  isOpen,
  onClose,
  position,
  theme,
  triggerRef
}) => {
  const {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    openDropdown,
    closeDropdown
  } = useNotifications({ limit: 100 });

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Control dropdown state in the hook
  useEffect(() => {
    if (isOpen) {
      openDropdown();
    } else {
      closeDropdown();
    }
    
    // Cleanup on unmount
    return () => {
      closeDropdown();
    };
  }, [isOpen, openDropdown, closeDropdown]);

  // Auto-mark notifications as read when dropdown is opened
  useEffect(() => {
    if (isOpen) {
      // Mark all unread notifications as read immediately
      const unreadNotifications = notifications.filter(notification => !notification.is_read);
      if (unreadNotifications.length > 0) {
        // Call markAllAsRead API
        markAllAsRead().catch(error => {
          logError('Error marking notifications as read', error);
        });
      }
    }
  }, [isOpen, notifications, markAllAsRead]); // Added notifications back to dependencies

  // Handle click outside to close (desktop only - mobile uses backdrop)
  useEffect(() => {
    if (isMobile) return; // Mobile uses backdrop click handler
    
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Don't close if clicking on the trigger button
      if (triggerRef?.current && triggerRef.current.contains(target)) {
        return;
      }
      
      // Don't close if clicking inside the dropdown
      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return;
      }
      
      // Close if clicking outside both the dropdown and trigger
      onClose();
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef, isMobile]);

  // Get notification icon based on type
  const getNotificationIcon = (type: string, priority: string) => {
    const iconClass = `h-4 w-4 ${
      priority === 'urgent' ? 'text-red-500' :
      priority === 'high' ? 'text-orange-500' :
      priority === 'medium' ? 'text-blue-500' :
      'text-gray-500'
    }`;

    switch (type) {
      case 'event_created':
        return <Info className={iconClass} />;
      case 'event_approved':
        return <CheckCircle className={iconClass} />;
      case 'event_rejected':
        return <XCircle className={iconClass} />;
      case 'guest_response':
        return <Check className={iconClass} />;
      case 'event_reminder':
        return <Clock className={iconClass} />;
      case 'system_alert':
        return <AlertCircle className={iconClass} />;
      default:
        return <Info className={iconClass} />;
    }
  };

  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'low':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Format time ago
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  // Handle notification actions
  const handleMarkAsRead = async (notification: Notification) => {
    if (notification.is_read) return;
    
    setActionLoading(notification.id);
    try {
      await markAsRead(notification.id);
    } catch (error) {
      logError('Error marking notification as read', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkAllAsRead = async () => {
    setActionLoading('mark_all');
    try {
      await markAllAsRead();
    } catch (error) {
      logError('Error marking all notifications as read', error);
    } finally {
      setActionLoading(null);
    }
  };

  // Archive function removed - no action buttons needed

  // Delete function removed - only archive functionality is available

  // Render modal in portal to ensure proper z-index and positioning
  // Use useMemo to prevent recreating the portal on every render
  // IMPORTANT: Must be called before any conditional returns
  const modalPortal = useMemo(() => {
    return createPortal(
      <NotificationSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        theme={theme}
      />,
      document.body
    );
  }, [showSettingsModal, theme]);

  // Calculate responsive positioning to prevent overflow (desktop only)
  const [adjustedPosition, setAdjustedPosition] = useState(position);

  useEffect(() => {
    if (isOpen && !isMobile) {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const padding = 16; // Minimum padding from edges
      
      // Estimate dropdown dimensions (will be adjusted after render)
      const estimatedWidth = 384; // Desktop width
      const estimatedHeight = 400; // Estimate

      let adjustedRight = position.right;
      let adjustedTop = position.top;

      // Ensure dropdown doesn't overflow on the right edge
      if (position.right + estimatedWidth > viewportWidth - padding) {
        adjustedRight = Math.max(padding, viewportWidth - estimatedWidth - padding);
      }

      // Ensure minimum padding from right edge
      if (adjustedRight < padding) {
        adjustedRight = padding;
      }

      // Ensure dropdown doesn't overflow on the bottom
      if (position.top + estimatedHeight > viewportHeight - padding) {
        adjustedTop = Math.max(padding, viewportHeight - estimatedHeight - padding);
      }

      setAdjustedPosition({ right: adjustedRight, top: adjustedTop });

      // Recalculate after a brief delay to get actual dimensions
      const timeoutId = setTimeout(() => {
        if (dropdownRef.current) {
          const rect = dropdownRef.current.getBoundingClientRect();
          const actualWidth = rect.width;
          const actualHeight = rect.height;

          let finalRight = adjustedRight;
          let finalTop = adjustedTop;

          // Final check with actual dimensions
          if (finalRight + actualWidth > viewportWidth - padding) {
            finalRight = Math.max(padding, viewportWidth - actualWidth - padding);
          }

          if (finalTop + actualHeight > viewportHeight - padding) {
            finalTop = Math.max(padding, viewportHeight - actualHeight - padding);
          }

          setAdjustedPosition({ right: finalRight, top: finalTop });
        }
      }, 10);

      return () => clearTimeout(timeoutId);
    } else {
      // Reset position when closed
      setAdjustedPosition(position);
    }
  }, [isOpen, position, isMobile]);

  // Handle window resize (desktop only)
  useEffect(() => {
    if (!isOpen || isMobile) return;

    const handleResize = () => {
      if (dropdownRef.current) {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const padding = 16;
        const rect = dropdownRef.current.getBoundingClientRect();

        let finalRight = adjustedPosition.right;
        let finalTop = adjustedPosition.top;

        // Recalculate with actual dimensions
        if (finalRight + rect.width > viewportWidth - padding) {
          finalRight = Math.max(padding, viewportWidth - rect.width - padding);
        }

        if (finalTop + rect.height > viewportHeight - padding) {
          finalTop = Math.max(padding, viewportHeight - rect.height - padding);
        }

        setAdjustedPosition({ right: finalRight, top: finalTop });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, adjustedPosition, isMobile]);

  if (!isOpen) {
    // Still render the modal even when dropdown is closed
    return modalPortal;
  }

  // Mobile: Full-screen modal with backdrop
  if (isMobile) {
    return (
      <>
        {modalPortal}
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[110]"
          onClick={onClose}
        />
        {/* Modal Content */}
        <div
          ref={dropdownRef}
          className={`fixed inset-0 z-[111] flex flex-col ${
            theme === 'dark'
              ? 'bg-gray-800'
              : 'bg-white'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`px-4 py-3 border-b flex-shrink-0 ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-100'
          }`}>
            <div className="flex items-center justify-between">
              <h3 className={`text-base font-semibold ${theme === 'dark' ? 'text-white' : 'text-black'}`}>
                Notifications
                {unreadCount > 0 && (
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                    theme === 'dark' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {unreadCount}
                  </span>
                )}
              </h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowSettingsModal(true);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  className={`text-xs p-1.5 rounded transition-colors relative z-10 ${
                    theme === 'dark'
                      ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                      : 'text-gray-500 hover:text-black hover:bg-gray-100'
                  }`}
                  title="Notification Settings"
                >
                  <Settings className="h-4 w-4 pointer-events-none" />
                </button>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    disabled={actionLoading === 'mark_all'}
                    className={`text-xs px-2 py-1 rounded transition-colors ${
                      theme === 'dark'
                        ? 'text-blue-400 hover:text-blue-300 hover:bg-gray-700'
                        : 'text-blue-600 hover:text-blue-500 hover:bg-gray-50'
                    } ${actionLoading === 'mark_all' ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <CheckCheck className="h-3 w-3" />
                  </button>
                )}
                <button
                  onClick={onClose}
                  className={`p-1 rounded transition-colors ${
                    theme === 'dark'
                      ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                      : 'text-gray-500 hover:text-black hover:bg-gray-100'
                  }`}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className={`px-4 py-8 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Loading notifications...
              </div>
            ) : error ? (
              <div className={`px-4 py-8 text-center ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
                {error}
              </div>
            ) : notifications.length === 0 ? (
              <div className={`px-4 py-8 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                No notifications yet
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`px-4 py-3 border-b transition-colors ${
                    theme === 'dark' 
                      ? 'border-gray-700 hover:bg-gray-700' 
                      : 'border-gray-100 hover:bg-gray-50'
                  } ${!notification.is_read ? (theme === 'dark' ? 'bg-gray-700/50' : 'bg-blue-50/50') : ''}`}
                >
                  <div className="flex items-start space-x-3">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(notification.type, notification.priority)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium break-words ${
                            theme === 'dark' ? 'text-white' : 'text-black'
                          }`}>
                            {notification.title}
                          </p>
                          <p className={`text-xs mt-1 break-words ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            {notification.message}
                          </p>
                          {notification.event_title && (
                            <p className={`text-xs mt-1 break-words ${
                              theme === 'dark' ? 'text-blue-300' : 'text-blue-600'
                            }`}>
                              Event: {notification.event_title}
                            </p>
                          )}
                          {notification.guest_name && (
                            <p className={`text-xs mt-1 break-words ${
                              theme === 'dark' ? 'text-green-300' : 'text-green-600'
                            }`}>
                              Guest: {notification.guest_name}
                            </p>
                          )}
                        </div>

                        {/* Priority badge */}
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium border flex-shrink-0 ${
                          getPriorityColor(notification.priority)
                        }`}>
                          {notification.priority}
                        </span>
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between mt-2">
                        <span className={`text-xs ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {formatTimeAgo(notification.created_at)}
                        </span>

                        {/* Actions */}
                        <div className="flex items-center space-x-1">
                          {!notification.is_read && (
                            <button
                              onClick={() => handleMarkAsRead(notification)}
                              disabled={actionLoading === notification.id}
                              className={`p-1 rounded transition-colors ${
                                theme === 'dark'
                                  ? 'text-gray-400 hover:text-white hover:bg-gray-600'
                                  : 'text-gray-500 hover:text-black hover:bg-gray-200'
                              } ${actionLoading === notification.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title="Mark as read"
                            >
                              <Check className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </>
    );
  }

  // Desktop: Existing dropdown behavior
  return (
    <>
    {modalPortal}
    <div
      ref={dropdownRef}
      className={`fixed w-[calc(100vw-2rem)] max-w-80 sm:max-w-96 rounded-xl shadow-xl border py-2 z-[110] ${
        theme === 'dark'
          ? 'bg-gray-800 border-gray-700'
          : 'bg-white border-gray-200'
      }`}
      style={{ 
        right: `${adjustedPosition.right}px`, 
        top: `${adjustedPosition.top}px`,
        maxHeight: 'calc(100vh - 5rem)'
      }}
    >
      {/* Header */}
      <div className={`px-4 py-3 border-b ${
        theme === 'dark' ? 'border-gray-700' : 'border-gray-100'
      }`}>
        <div className="flex items-center justify-between">
          <h3 className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-black'}`}>
            Notifications
            {unreadCount > 0 && (
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                theme === 'dark' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {unreadCount}
              </span>
            )}
          </h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowSettingsModal(true);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              className={`text-xs p-1.5 rounded transition-colors relative z-10 ${
                theme === 'dark'
                  ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                  : 'text-gray-500 hover:text-black hover:bg-gray-100'
              }`}
              title="Notification Settings"
            >
              <Settings className="h-4 w-4 pointer-events-none" />
            </button>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                disabled={actionLoading === 'mark_all'}
                className={`text-xs px-2 py-1 rounded transition-colors ${
                  theme === 'dark'
                    ? 'text-blue-400 hover:text-blue-300 hover:bg-gray-700'
                    : 'text-blue-600 hover:text-blue-500 hover:bg-gray-50'
                } ${actionLoading === 'mark_all' ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <CheckCheck className="h-3 w-3" />
              </button>
            )}
            <button
              onClick={onClose}
              className={`p-1 rounded transition-colors ${
                theme === 'dark'
                  ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                  : 'text-gray-500 hover:text-black hover:bg-gray-100'
              }`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-h-[calc(100vh-12rem)] sm:max-h-96 overflow-y-auto">
        {loading ? (
          <div className={`px-4 py-8 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            Loading notifications...
          </div>
        ) : error ? (
          <div className={`px-4 py-8 text-center ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
            {error}
          </div>
        ) : notifications.length === 0 ? (
          <div className={`px-4 py-8 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            No notifications yet
          </div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              className={`px-4 py-3 border-b transition-colors ${
                theme === 'dark' 
                  ? 'border-gray-700 hover:bg-gray-700' 
                  : 'border-gray-100 hover:bg-gray-50'
              } ${!notification.is_read ? (theme === 'dark' ? 'bg-gray-700/50' : 'bg-blue-50/50') : ''}`}
            >
              <div className="flex items-start space-x-3">
                {/* Icon */}
                <div className="flex-shrink-0 mt-0.5">
                  {getNotificationIcon(notification.type, notification.priority)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium break-words ${
                        theme === 'dark' ? 'text-white' : 'text-black'
                      }`}>
                        {notification.title}
                      </p>
                      <p className={`text-xs mt-1 break-words ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {notification.message}
                      </p>
                      {notification.event_title && (
                        <p className={`text-xs mt-1 break-words ${
                          theme === 'dark' ? 'text-blue-300' : 'text-blue-600'
                        }`}>
                          Event: {notification.event_title}
                        </p>
                      )}
                      {notification.guest_name && (
                        <p className={`text-xs mt-1 break-words ${
                          theme === 'dark' ? 'text-green-300' : 'text-green-600'
                        }`}>
                          Guest: {notification.guest_name}
                        </p>
                      )}
                    </div>

                    {/* Priority badge */}
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium border flex-shrink-0 ${
                      getPriorityColor(notification.priority)
                    }`}>
                      {notification.priority}
                    </span>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between mt-2">
                    <span className={`text-xs ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {formatTimeAgo(notification.created_at)}
                    </span>

                    {/* Actions */}
                    <div className="flex items-center space-x-1">
                      {!notification.is_read && (
                        <button
                          onClick={() => handleMarkAsRead(notification)}
                          disabled={actionLoading === notification.id}
                          className={`p-1 rounded transition-colors ${
                            theme === 'dark'
                              ? 'text-gray-400 hover:text-white hover:bg-gray-600'
                              : 'text-gray-500 hover:text-black hover:bg-gray-200'
                          } ${actionLoading === notification.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title="Mark as read"
                        >
                          <Check className="h-3 w-3" />
                        </button>
                      )}
                      {/* Archive button removed - no action buttons needed */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
    </>
  );
};

export default NotificationDropdown;
